import { STORAGE_KEYS } from '@agos/api-client'
import { T, tt } from '@agos/shared'
import { Spinner } from '@agos/ui'
import { AlertTriangle } from 'lucide-react'
import { useEffect, useState } from 'react'

import { fallbackPath, handoffMessage, landingPath, readHandoff, type HandoffOutcome } from './handoff'

/**
 * `/auth-token` — the Control Tower's *Otvoriť manažér* button lands here with `?t=<jwt>`.
 *
 * §14.5 asks that this flow be specified before it is ported; `handoff.ts` is that spec, and FB-179 is what
 * specifying it turned up. The short version:
 *
 * - the cached user and permissions are cleared, not just the token overwritten, so the shell does not paint
 *   the previous account's menu while holding the new account's token;
 * - the token's **shape** is checked, so a truncated URL says so instead of booting into a 401 loop;
 * - the redirect honours `BASE_URL`;
 * - the hard reload is kept, because `AuthProvider` reads the token once on mount.
 *
 * Nothing here calls the API. The token is adopted and the page reloads; `/auth/me` is what proves it.
 */
export function AuthTokenPage() {
  // Read once, during the first render: the query string does not change while this page is mounted, and
  // setting it from the effect would paint an empty screen and then re-render for no reason.
  const [outcome] = useState<HandoffOutcome>(() => readHandoff(window.location.search))

  useEffect(() => {
    const result = outcome

    if (result.kind !== 'accepted') {
      // A malformed token is worth a moment on screen — otherwise the bounce to the login form looks like
      // the link simply did nothing, and the person tries it again.
      const wait = result.kind === 'malformed' ? 4000 : 0
      const timer = setTimeout(() => {
        window.location.replace(fallbackPath(import.meta.env.BASE_URL))
      }, wait)
      return () => clearTimeout(timer)
    }

    try {
      localStorage.setItem(STORAGE_KEYS.token, result.token)
      // FB-179: the provider renders from the cached user before `/auth/me` answers, so leaving the previous
      // one behind paints their name, their role and their menu under the new token.
      localStorage.removeItem(STORAGE_KEYS.user)
    } catch {
      // A browser with storage disabled cannot hold a session at all; the login form says so.
      window.location.replace(fallbackPath(import.meta.env.BASE_URL))
      return
    }

    // Hard reload, not a client-side navigation: `AuthProvider` reads the token once, on mount.
    window.location.replace(landingPath(import.meta.env.BASE_URL))
    return
  }, [outcome])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="text-center space-y-3" data-handoff={outcome.kind}>
        {outcome.kind === 'accepted' ? (
          <>
            <Spinner className="mx-auto" />
            <p className="text-sm text-muted-foreground">
              <T>Prihlasujem do manažéra…</T>
            </p>
          </>
        ) : (
          <>
            <AlertTriangle
              className={outcome.kind === 'malformed' ? 'w-8 h-8 text-amber-500 mx-auto' : 'w-8 h-8 text-muted-foreground mx-auto'}
            />
            <p className="text-sm text-muted-foreground max-w-sm">{handoffMessage(outcome)}</p>
            <p className="text-xs text-muted-foreground/60">
              <T>Presmerujem na prihlásenie…</T>
            </p>
          </>
        )}
        <span className="sr-only">{tt('Control Tower prihlásenie')}</span>
      </div>
    </div>
  )
}
